import { Error as ErrorIcon, Info as InfoIcon, WarningAmber } from '@mui/icons-material';
import { Box, Grid, useEventCallback } from '@mui/material';
import { BodyExceptionListVM } from 'practice/view-models/practice-detail';
import { memo, ReactElement, ReactNode, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import { noop } from 'shell/utils/noop';

interface TableExceptionListProps {
  hasIcons?: boolean;
  data?: Array<BodyExceptionListVM>;
}

const headCells: HeadCell<BodyExceptionListVM, keyof BodyExceptionListVM>[] = [
  {
    id: 'exceptionDetail',
    label: 'Dettaglio eccezioni',
  },
  {
    id: 'exceptionDate',
    label: 'Date e ora',
  },
  {
    id: 'tipoSegnalazione',
    label: 'Tipo Segnalazione',
  },
];

const iconTipoSegnalazione = (tipoMessaggio?: string): ReactNode => {
  switch (tipoMessaggio) {
    case 'ERROR':
      return <ErrorIcon color="error" fontSize="small" titleAccess="Errore" />;
    case 'WARN':
      return <WarningAmber sx={{ color: 'warning.main' }} fontSize="small" titleAccess="Warning" />;
    case 'INFO':
      return <InfoIcon color="info" fontSize="small" titleAccess="Info" />;
    default:
      return null;
  }
};

const TableExceptionListComponent = ({ data, hasIcons }: TableExceptionListProps): ReactElement => {
  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const body: Array<BodyExceptionListVM> = useMemo(() => {
    return (data || [])
      .map((item) => ({
        ...item,
        exceptionDetail: item.isCadastralAddress ? (
          <Box component="span" sx={{ pl: 4 }}>
            {item.exceptionDetail}
          </Box>
        ) : (
          item.exceptionDetail
        ),
        tipoSegnalazione: item.isCadastralAddress ? null : iconTipoSegnalazione(item.tipoMessaggio),
      }))
      .filter((item) => (!hasIcons ? true : item.isCadastralAddress || item.tipoSegnalazione));
  }, [data, hasIcons]);

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {body?.length > 0 ? (
          <Table
            ariaLabel="Elenco Eccezioni"
            page={tableProps.page || 0}
            rowsPerPage={tableProps.rowsPerPage || 10}
            body={body}
            count={body?.length || 0}
            head={headCells.filter((h) => (!hasIcons ? h.id !== 'tipoSegnalazione' : true))}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          'Nessuna informazione'
        )}
      </Grid>
    </Grid>
  );
};

TableExceptionListComponent.displayName = 'Practice Research - Table Persons component';

const TableExceptionList = memo(TableExceptionListComponent);

export default TableExceptionList;
